import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sendInvitationToContact } from 'src/store/actions/contacts';
import { getAllUserTypeRoles } from 'src/store/actions/formOptions';
import { selectFormOptions } from 'src/store/selectors/formOptions';
import ContactsTable from '../../components/ContactsTable';
import Heading from '../../components/Heading';
import Tabs from '../../components/Tabs';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { clearSearch } from 'src/store/actions/search';
import { selectLoader } from 'src/store/selectors/loader';
import Loader from '../../components/Loader';
import { variables } from 'src/Tenants';
import { stopLoaders } from 'src/store/actions/loader';
import {
  clearActiveCatalogTab,
  clearActiveContactTab,
  clearActiveSuiteTab,
  clearActiveTab,
} from 'src/store/actions/activeTab';
import {
  useContactsPageOrchestrator,
  IUseContactsPageOrchestratorReturn,
} from 'src/pages/Contacts/CustomHooks/useContactsPageOrchestrator';
import { ContactFilterOverlay } from 'src/pages/Contacts/ContactFilterModal/ContactFilterModal';
import { IContact } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import useProperties from 'src/utils/CommonDataFetchingUtils/useProperties';
import { ContactsSuiteActionsProvider } from 'src/pages/Contacts/ContactsContext/ContactsContext';
import { ContactFormProvider } from 'src/SharedCommonContext/ContactFormContext';
import { PropertiesForContactsProvider } from 'src/pages/Contacts/ContactsContext/PropertiesForContactsContext';
import {
  sendActivationLink,
  sendResetPasswordLink,
} from 'src/utils/CommonHelpers/ContactAPIOperations/commonContactDataHelpers';
import WithNavigation from 'src/layouts/WithNavigation/WithNavigation';

const ContactsContent = () => {
  const formOptions: any = useSelector(selectFormOptions);
  const user: any = useSelector(selectAuthenticatedUser);
  const loader: any = useSelector(selectLoader);

  const {
    contacts,
    isLoadingContacts,
    refetchContacts,
    deleteContactById,
    basicSearchTerm,
    updateBasicSearchTerm,
    activeFilters,
    handleApplyFilters,
    resetFilters,
    removeSingleActiveFilter,
    hasActiveFilters,
    uniqueContactStatuses,
    canEditContactEmail,
  }: IUseContactsPageOrchestratorReturn = useContactsPageOrchestrator();

  const onDeleteContactById = async (data: any): Promise<void> => {
    await deleteContactById(data?.id);
  };

  const [selectedContacts, setSelectedContacts] = useState<any[]>([]);
  const [allSelected, setAllSelected] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState<boolean>(false);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(stopLoaders());
    dispatch(getAllUserTypeRoles());
    dispatch(clearSearch());
  }, [dispatch, user.id, user.roles]);

  useEffect(() => {
    dispatch(clearActiveTab());
    dispatch(clearActiveSuiteTab());
    dispatch(clearActiveCatalogTab());
    dispatch(clearActiveContactTab());
  }, [dispatch]);

  const { data: properties, fetchProperties } = useProperties();

  useEffect(() => {
    void fetchProperties();
  }, []);

  const toggleFilterModal = () => {
    setShowFilterModal(!showFilterModal);
  };

  const onSelectAllContacts = () => {
    if (allSelected) {
      setSelectedContacts([]);
      setAllSelected(false);
    } else {
      const allContactsSelected = contacts.map((contact: IContact) => ({
        id: contact.id,
      }));

      setSelectedContacts(allContactsSelected);
      setAllSelected(true);
    }
  };

  const onSelectContact = (userId: any) => {
    let updatedContacts: any[] = [...selectedContacts];
    let index: any = updatedContacts.findIndex((sc: any) => sc.id === userId);

    if (index === -1) {
      updatedContacts = [...updatedContacts, { id: userId }];
    } else {
      updatedContacts = updatedContacts.filter((sc: any) => sc.id !== userId);
    }

    setSelectedContacts(updatedContacts);

    const allContactIds: string[] = contacts.map((contact: IContact) => {
      return contact.id;
    });

    const selectedContactIds = updatedContacts.map((contact: any) => {
      return contact.id;
    });

    const areAllSelected =
      allContactIds.every((id: string) => selectedContactIds.includes(id)) &&
      allContactIds.length > 0;

    setAllSelected(areAllSelected);
  };

  const onSendInvitation = async () => {
    let params: any = {
      userId: user.id,
      users: selectedContacts,
    };

    dispatch(sendInvitationToContact(params));
    setSelectedContacts([]);
  };

  const contactRoleCounts = useMemo(() => {
    const counts = {
      propertyManagers: 0,
      serviceProviders: 0,
      homeOwners: 0,
      realtors: 0,
      vendors: 0,
      strataManagers: 0,
      occupants: 0,
      serviceManagers: 0,
    };

    if (contacts && contacts.length > 0) {
      contacts.forEach((contact: any) => {
        const role = contact?.roles?.[0]?.role?.name || '';

        switch (role) {
          case 'Service Provider':
            counts.serviceProviders++;
            break;
          case 'Property Developer':
            counts.propertyManagers++;
            break;
          case 'Home Owner':
            counts.homeOwners++;
            break;
          case 'Realtor':
            counts.realtors++;
            break;
          case 'Vendor':
            counts.vendors++;
            break;
          case 'Strata Manager':
            counts.strataManagers++;
            break;
          case 'Occupant':
            counts.occupants++;
            break;
          case 'Service Manager':
            counts.serviceManagers++;
            break;
        }
      });
    }

    return counts;
  }, [contacts]);

  const headingItems: any = useMemo(
    () => [
      {
        key: variables.propertyDevelopers,
        value: contactRoleCounts.propertyManagers,
      },
      {
        key: variables.strataManagers,
        value: contactRoleCounts.strataManagers,
      },
      {
        key: 'Service Providers',
        value: contactRoleCounts.serviceProviders,
      },
      {
        key: variables.homeOwnerss,
        value: contactRoleCounts.homeOwners,
      },
      {
        key: 'Realtor',
        value: contactRoleCounts.realtors,
      },
      {
        key: 'Occupants',
        value: contactRoleCounts.occupants,
      },
      {
        key: 'Vendors',
        value: contactRoleCounts.vendors,
      },
      {
        key: 'Service Managers',
        value: contactRoleCounts.serviceManagers,
      },
    ],
    [contactRoleCounts]
  );

  return (
    <ContactFormProvider>
      <PropertiesForContactsProvider>
        <>
          {loader ? <Loader center={true} /> : null}
          <WithNavigation
            title="Contacts"
            top={
              <Heading
                headline="Contacts"
                items={headingItems}
                loading={isLoadingContacts}
              />
            }
            bottom={
              <Tabs
                items={[
                  {
                    label: 'Contacts',
                    length: contacts.length,
                    content: (
                      <ContactsTable
                        contacts={contacts}
                        onDeleteContact={(data: any) =>
                          onDeleteContactById(data)
                        }
                        sendResetPasswordLinkToUser={sendResetPasswordLink}
                        sendActivationLinkToUser={sendActivationLink}
                        onSelectContact={onSelectContact}
                        selectedContacts={selectedContacts}
                        onSendInvitation={onSendInvitation}
                        onShowFilterModal={toggleFilterModal}
                        activeFilters={activeFilters}
                        resetFilters={resetFilters}
                        removeSingleActiveFilter={removeSingleActiveFilter}
                        hasActiveFilters={hasActiveFilters}
                        refetchContactFormData={refetchContacts}
                        allSelected={allSelected}
                        onSelectAllContacts={onSelectAllContacts}
                        isLoadingContacts={isLoadingContacts}
                        basicContactFilterString={basicSearchTerm}
                        updateBasicContactFilterString={updateBasicSearchTerm}
                        canEditContactEmail={canEditContactEmail}
                      />
                    ),
                  },
                ]}
                activeTab={undefined}
                from={undefined}
              />
            }
          />
          <ContactFilterOverlay
            showFilterModal={showFilterModal}
            setShowFilterModal={setShowFilterModal}
            onApplyFilters={handleApplyFilters}
            activeFilters={activeFilters}
            properties={properties}
            formOptions={formOptions}
            uniqueContactStatuses={uniqueContactStatuses}
          />
        </>
      </PropertiesForContactsProvider>
    </ContactFormProvider>
  );
};

const Contacts = () => {
  return (
    <ContactsSuiteActionsProvider>
      <ContactsContent />
    </ContactsSuiteActionsProvider>
  );
};

export default Contacts;
